import type { ViewAdminUser } from '../../api/viewAdminApiService';
import { useViewAdminContent } from '../../hooks/useViewAdminContent';

interface ViewAdminContentProps {
  user: ViewAdminUser | null;
}

const ViewAdminContent = ({ user }: ViewAdminContentProps) => {
  const {
    formattedUserId,
    formattedEmail,
    formattedPhoneNumber,
    formattedRole,
    formattedCreatedAt,
    formattedAddressLine1,
    formattedAddressLine2,
    formattedCity,
    formattedState,
    formattedZipCode,
    formattedCountry,
  } = useViewAdminContent(user);
  return (
    <div className="flex flex-col gap-6 m-[6px]">
      <div className="flex flex-col gap-4 border-[1px] border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-[18px] font-normal text-on-surface-variant"
          aria-label="Podgląd Administratora"
          data-testid="view-admin-content-title"
        >
          Podgląd Administratora
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          }}
        >
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Admin ID"
            >
              Admin ID
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Admin ID"
            >
              {formattedUserId}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Adres e-mail"
            >
              Adres e-mail
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Adres e-mail"
            >
              {formattedEmail}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Numer telefonu"
            >
              Numer telefonu
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Numer telefonu"
            >
              {formattedPhoneNumber}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Rola"
            >
              Rola
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Rola"
            >
              {formattedRole}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Data utworzenia"
            >
              Data utworzenia
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Data utworzenia"
            >
              {formattedCreatedAt}
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 border-[1px] border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-[18px] font-normal text-on-surface-variant"
          aria-label="Dane adresowe"
          data-testid="view-admin-address-title"
        >
          Dane adresowe
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          }}
        >
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Adres (linia 1)"
            >
              Adres (linia 1)
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Adres (linia 1)"
            >
              {formattedAddressLine1}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Adres (linia 2)"
            >
              Adres (linia 2)
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Adres (linia 2)"
            >
              {formattedAddressLine2}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Miejscowość"
            >
              Miejscowość
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Miejscowość"
            >
              {formattedCity}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Województwo"
            >
              Województwo
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Województwo"
            >
              {formattedState}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Kod pocztowy"
            >
              Kod pocztowy
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Kod pocztowy"
            >
              {formattedZipCode}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Kraj"
            >
              Kraj
            </h2>
            <p
              className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Kraj"
            >
              {formattedCountry}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAdminContent;
